import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

/**
 * Destructive-action confirmation: the user must type the item's exact name to enable the button.
 * `onConfirm` may return a promise; the dialog closes itself when it resolves.
 */
export function ConfirmDeleteDialog({ open, onOpenChange, name, title = "Delete", description, verb = "Delete", onConfirm, testid = "confirm-delete" }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const matches = typed.trim() === (name || "").trim();

  useEffect(() => {
    if (open) { setTyped(""); setBusy(false); }
  }, [open]);

  const confirm = async (e) => {
    e.preventDefault();
    if (!matches || busy) return;
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md" data-testid={`${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <span className="grid h-8 w-8 place-items-center bg-rose-50 text-rose-700"><AlertTriangle className="h-4 w-4" /></span>
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={confirm} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">
              Type <span className="font-mono-tight normal-case tracking-normal text-slate-900">{name}</span> to confirm
            </Label>
            <Input autoFocus autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={name} className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" data-testid={`${testid}-input`} />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid={`${testid}-cancel`}>Cancel</Button>
            <Button type="submit" disabled={!matches || busy} className="h-10 bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-40" data-testid={`${testid}-submit`}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{verb}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Small helper hook: `const del = useConfirmDelete(); del.ask(item); <ConfirmDeleteDialog {...del.props} />` */
export function useConfirmDelete() {
  const [item, setItem] = useState(null);
  return {
    item,
    ask: setItem,
    props: { open: !!item, onOpenChange: (o) => !o && setItem(null) },
  };
}
